import BpkPrice, {
  SIZES,
  ALIGNS as PRICE_ALIGNS,
} from '@skyscanner/backpack-web/bpk-component-price';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text/src/BpkText';

import GreatPricesNote from '../GreatPricesNote';

import type { OnTrackPriceSelected } from '../types';
import type { PriceBreakdownInfo } from '@shared/types/frontend/price-breakdown';
import type {
  Offer,
  PriceType,
  RatePrice,
} from '@shared/types/frontend/ubff/hotel-detail-price';
import type { I18nService } from '@shared/types/i18n';

import {
  getCugTag,
  getGreatPriceTag,
  getVisibleCugLabelTag,
} from '@hotels-hotel-detail/utils/hotel-offers';
import TotalPriceBreakdown, {
  ALIGNS,
} from '@shared/ui/components/TotalPriceBreakdown';
import { withI18n } from '@shared/ui/contexts/i18n';
import { CLICK_AREA } from '@shared/utils-frontend/mini-events/constants';
import { getPriceTypeText } from '@shared/utils-universal/price-type';

type Props = {
  i18n: I18nService;
  offer: Offer;
  isMobile?: boolean;
  costInfo?: PriceBreakdownInfo;
  priceType?: PriceType;
  onTrackPriceSelected: OnTrackPriceSelected;
};

export const getLeadingText = ({
  isMobile,
  price,
  showPercentageOff,
}: {
  price: RatePrice;
  isMobile?: boolean;
  showPercentageOff?: boolean;
}) => {
  const { discountInfo, tags } = price;
  if (!discountInfo) {
    return undefined;
  }
  const showPercentageOffTag = showPercentageOff || !getGreatPriceTag(tags);
  if (isMobile) {
    const nameCug = getVisibleCugLabelTag(tags);
    return nameCug?.label;
  }
  if (showPercentageOffTag) {
    const percentageOffTag = getCugTag(tags);
    return percentageOffTag?.subLabel;
  }
  return undefined;
};

const PriceInfo = ({
  costInfo,
  i18n,
  isMobile,
  offer,
  onTrackPriceSelected,
  priceType,
}: Props) => {
  const {
    price: { discountInfo, price, tags },
    url,
  } = offer;

  const greatPriceTag = getGreatPriceTag(tags);
  const nameCug = getVisibleCugLabelTag(tags);
  const previousPrice =
    !(greatPriceTag && !isMobile) && discountInfo?.priceWithoutDiscount
      ? i18n.formatCurrency(discountInfo.priceWithoutDiscount)
      : undefined;
  const leadingText = getLeadingText({
    price: offer.price,
    isMobile,
  });

  return (
    <>
      {!isMobile && greatPriceTag && (
        <GreatPricesNote
          label={greatPriceTag.label}
          subLabel={greatPriceTag.subLabel}
          description={greatPriceTag.tip}
        />
      )}
      {!isMobile && !greatPriceTag && nameCug?.label && (
        <BpkText
          textStyle={TEXT_STYLES.footnote}
          color={TEXT_COLORS.textSecondary}
        >
          {nameCug.label}
        </BpkText>
      )}
      {costInfo ? (
        <TotalPriceBreakdown
          isMobile={!!isMobile}
          costInfo={costInfo}
          leadingText={leadingText}
          previousPriceNode={previousPrice}
          deeplinkUrl={url}
          onTrackPriceSelected={onTrackPriceSelected}
          clickArea={CLICK_AREA.DETAIL_GENERAL_CTA}
          align={ALIGNS.right}
        />
      ) : (
        <BpkPrice
          size={SIZES.small}
          price={i18n.formatCurrency(price)}
          previousPrice={previousPrice}
          leadingText={leadingText}
          trailingText={getPriceTypeText(i18n, priceType)}
          align={PRICE_ALIGNS.right}
        />
      )}
    </>
  );
};

export default withI18n(PriceInfo);
